import { useEffect, useRef } from "react";

/** Platform modifier label for shortcut hints (`⌘` on Apple devices, `Ctrl` elsewhere). */
export const MOD: string = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
/** Prefix for inline hints ("⌘S" on Apple devices, "Ctrl+S" elsewhere). */
export const MOD_PREFIX: string = MOD === "⌘" ? "⌘" : "Ctrl+";

export interface ShortcutHandlers {
  /** `⌘K` / `Ctrl+K`. */
  onPalette: () => void;
  /** `/` outside inputs. */
  onSearch: () => void;
  /** `?` outside inputs. */
  onHelp: () => void;
  /** `g` followed by a letter within 800 ms. */
  onGo: (letter: string) => void;
  /** `⌘S` / `Ctrl+S` – the active editor decides what "save" means. */
  onSave?: () => void;
  /** `⌘J` / `Ctrl+J` – apps use it for "new conversation" / "new item". */
  onNew?: () => void;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable || target.closest("[cmdk-root]") != null;
}

/** Global app shortcuts. Sequences (`g s`) work like GitHub's; single keys never fire while typing. */
export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  const ref = useRef(handlers);
  ref.current = handlers;
  const pending = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current.onPalette();
        return;
      }
      if (mod && e.key.toLowerCase() === "s" && ref.current.onSave) {
        e.preventDefault();
        ref.current.onSave();
        return;
      }
      if (mod && e.key.toLowerCase() === "j" && ref.current.onNew) {
        e.preventDefault();
        ref.current.onNew();
        return;
      }
      if (mod || e.altKey || isEditable(e.target)) return;

      if (pending.current !== null) {
        window.clearTimeout(pending.current);
        pending.current = null;
        if (/^[a-z,]$/i.test(e.key)) {
          e.preventDefault();
          ref.current.onGo(e.key.toLowerCase());
          return;
        }
      }
      if (e.key === "g") {
        pending.current = window.setTimeout(() => (pending.current = null), 800);
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        ref.current.onSearch();
      } else if (e.key === "?") {
        e.preventDefault();
        ref.current.onHelp();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

/** Registry so the page that owns a draft can answer `⌘S`. */
const saveHandlers = new Set<() => void>();
export function registerSaveHandler(fn: () => void): () => void {
  saveHandlers.add(fn);
  return () => saveHandlers.delete(fn);
}
export function runSaveHandlers(): boolean {
  if (!saveHandlers.size) return false;
  saveHandlers.forEach((fn) => fn());
  return true;
}

// ---------------------------------------------------------------- recent entries (palette history)
export interface RecentEntry {
  href: string;
  label: string;
}

const MAX_RECENT = 6;

/** Remembers a visited destination for the palette's "Recent" group. Storage key is per app (e.g. `ncp.admin.recent`). */
export function pushRecent(storageKey: string, entry: RecentEntry): void {
  try {
    const list = readRecent(storageKey).filter((r) => r.href !== entry.href);
    list.unshift(entry);
    localStorage.setItem(storageKey, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    /* storage unavailable */
  }
}

export function readRecent(storageKey: string): RecentEntry[] {
  try {
    return JSON.parse(localStorage.getItem(storageKey) ?? "[]") as RecentEntry[];
  } catch {
    return [];
  }
}
